import { useMemo } from 'react';
import type { CompanyAnalysis, MarketContext } from '../types/geminiTypes';
import type { ValueAssumptions } from '../types/analysis';
import { safeToFixed } from '../lib/formatters';

export interface DcfModel {
  projections: { year: number; fcf: number; pv: number; growth: number }[];
  intrinsicValuePerShare: number;
  strategicValuePerShare: number;
  isEstimable: boolean;
  buyUnderPrice: number;
  effectiveMarginOfSafety: number;
  enterpriseValue: number;
  pvTerminalValue: number;
  totalPv: number;
  fairValueLow: number;
  fairValueHigh: number;
  fairValueRange: {
    low: number;
    high: number;
    errorPercent: number;
    rationale: string;
  };
  strategicUpsideRange: {
    low: number;
    high: number;
  };
  strategicDownsideRange: {
    low: number;
    high: number;
  };
  buyUnderRationale: string;
  valuationStatus: {
    diffPercent: number;
    label: string;
    statusColor: string;
    isUndervalued: boolean;
  };
  premiums: {
    durability: number;
    optionality: number;
    qualityDiscount: number;
    terminalGrowth: number;
  };
  decision: {
    action: string;
    color: string;
    reason: string;
    qualifier?: string;
  };
  scenarios: {
    bear: number;
    base: number;
    bull: number;
  };
  fcfPerShare: number;
  exitMultiple: number;
  moatScore: number;
  predictabilityScore: number;
  userScenarioIV: number;
  userScenarioBuyUnder: number;
}

export function useDcfModel(
  analysis: CompanyAnalysis | null,
  marketContext: MarketContext | null,
  assumptions: ValueAssumptions
): DcfModel | null {
  return useMemo(() => {
    if (!analysis) return null;

    // Map qualitative scorecard values to numbers for the model
    const mapScoreToNumber = (key: string, value: any): number => {
      if (typeof value === 'number') return value;
      const mappings: Record<string, Record<string, number>> = {
        moat: { "Wide": 5, "Narrow": 3, "None": 1, "Eroding": 2 },
        management: { "Exceptional": 5, "Good": 4, "Questionable": 2, "Poor": 1 },
        understandable: { "Yes": 5, "Moderate": 3, "No": 1 },
        longTerm: { "High": 5, "Medium": 3, "Low": 2, "None": 1 },
        marginOfSafety: { "Massive": 5, "Extreme": 5, "Adequate": 4, "Moderate": 3, "Insufficient": 2, "None": 1 },
        stability: {
          // Low instability risk → high score
          "Highly Stable":        5,
          "Stable":               4,
          // Moderate instability risk → middle score
          "Moderate":             3,
          "High-Margin Compounder": 3,
          // High instability risk → low score
          "Cyclical":             2,
          "Volatile":             1,
          // Legacy keys preserved for backward compatibility
          "Low":                  5,
          "High":                 1
        }
      };
      return mappings[key]?.[value] || 3;
    };

    const projections = [];
    const calculatedFcfPs = analysis.sharesOutstanding > 0 ? analysis.lastYearFcf / analysis.sharesOutstanding : 0;
    const fcfPerShare = Math.max(
      analysis.oracleMetrics?.fcfPerShare || 0,
      (analysis.oracleMetrics?.ownerEarnings && analysis.sharesOutstanding > 0 ? analysis.oracleMetrics.ownerEarnings / analysis.sharesOutstanding : 0),
      calculatedFcfPs
    );
    
    // Smarter Valuation: Include Buyback Yield as a growth multiplier for per-share value
    const buybackYield = analysis.avgBuybackYield || 0;
    
    const moatScore = mapScoreToNumber('moat', analysis.scorecard?.moat);
    const predictabilityScore = mapScoreToNumber('longTerm', analysis.scorecard?.longTerm);
    const stabilityScore = mapScoreToNumber('stability', analysis.scorecard?.stability);

    // Smarter Valuation: Durability & Optionality Premiums
    // Buffett/Munger often pay up for "Inevitable" growth or hidden optionality
    // Capping these to avoid extreme valuations
    const durabilityPremium = (analysis.isGrowthStructurallyInevitable || analysis.isInevitableCompounderOverride) ? 1.5 : 0;
    const optionalityPremium = analysis.optionalityMultiplier?.hasHighOptionality ? 1.0 : 0;
    
    // Base Growth: No premiums (Conservative)
    // For elite businesses, we trust the durable growth more
    const buybackMultiplier = (moatScore >= 5) ? 0.8 : 0.6;
    const baseGrowth = assumptions.revenueGrowth + (buybackYield * buybackMultiplier);
    // Strategic Growth: Avoid overlaying too many assumptions
    const strategicGrowth = assumptions.revenueGrowth + buybackYield + durabilityPremium;
    
    // Smarter Valuation: Quality & Stability Adjusted Discount Rate
    // If moat is wide and predictability is high, we can justify a lower discount rate
    const qualityPremium = (moatScore >= 5 && predictabilityScore >= 5) ? -1.5 : (moatScore >= 4 && predictabilityScore >= 4) ? -0.75 : 0;
    const stabilityPremium = (stabilityScore <= 2) ? 1.5 : (stabilityScore === 3) ? 0.75 : 0;
    
    // Minimum discount rate of 7.0% for elite businesses, 8.5% for others
    const minDiscountRate = (moatScore >= 5 && predictabilityScore >= 5) ? 7.0 : 8.5;
    const maxDiscountRate = 15;
    const adjustedDiscountRate = Math.min(maxDiscountRate, Math.max(minDiscountRate, assumptions.discountRate + qualityPremium + stabilityPremium));

    // Smarter Valuation: Terminal Growth Adjustment
    // High quality, durable businesses can grow at GDP rates (2.0-2.5%) indefinitely
    // Terminal growth > 3% often leads to unrealistic valuations for mega-caps.
    const adjustedTerminalGrowth = (moatScore >= 5)
      ? Math.min(assumptions.terminalGrowth, 2.5) // Lowered from 3.5 to avoid $5T valuations
      : (moatScore >= 4 && stabilityScore >= 4) 
      ? Math.min(assumptions.terminalGrowth, 2.0) 
      : Math.min(assumptions.terminalGrowth, 1.5);

    // Engine IV: Use the deterministic DCF engine output as the primary IV anchor.
    // analysis.scenarios.base.intrinsicValuePerShare is computed by the TypeScript DCF
    // engine in runMathematician — the same value locked into the memo by IV-LOCK-RATIONALE.
    // analysis.oracleFramework?.oracleIntrinsicValue is the Oracle's own estimate which
    // can diverge (the dashboard previously used this, causing memo/dashboard mismatch).
    const engineIV = analysis.scenarios?.base?.intrinsicValuePerShare || 0;
    const legacyIV = (analysis as any).intrinsicValue;
    const aiSuggestedIV = engineIV > 0 ? engineIV : (analysis.oracleFramework?.oracleIntrinsicValue || legacyIV || 0);
    
    // Anchor on FMP Custom DCF if available and it makes sense
    const fmpCustomDcf = analysis.fmpDcf?.customDcf || 0;

    // A business is not estimable if FCF is 0/negative, or sharesOutstanding/price is missing
    // Exception: If AI has provided a suggested intrinsic value, we consider it estimable via Oracle Math
    const isEstimable = (fcfPerShare > 0 || aiSuggestedIV > 0 || fmpCustomDcf > 0) && 
                        (analysis.sharesOutstanding > 0 || analysis.sharesOutstanding === undefined) &&
                        analysis.currentPrice > 0 &&
                        !analysis.isType5DataContaminated;

    // Smarter Valuation: Growth Fade Logic
    // Growth rarely stays high for 10 years. We fade from year 6 to 10.
    const calculateIntrinsicValue = (initialGrowth: number, marginExp: number = 0) => {
      let fcfPs = fcfPerShare;
      let pvSum = 0;
      const yearProjections = [];
      
      const effectiveGrowth = initialGrowth + marginExp;

      for (let i = 1; i <= 10; i++) {
        // Fade growth from year 6 to 10 towards terminal growth
        let currentYearGrowth = effectiveGrowth;
        if (i > 5) {
          const fadeFactor = (i - 5) / 5; // 0.2, 0.4, 0.6, 0.8, 1.0
          currentYearGrowth = effectiveGrowth * (1 - fadeFactor) + adjustedTerminalGrowth * fadeFactor;
        }
        
        fcfPs = fcfPs * (1 + currentYearGrowth / 100);
        const pv = fcfPs / Math.pow(1 + adjustedDiscountRate / 100, i);
        pvSum += pv;
        yearProjections.push({ year: i, fcf: fcfPs * analysis.sharesOutstanding, pv: pv * analysis.sharesOutstanding, growth: currentYearGrowth });
      }

      // Smarter Valuation: Hybrid Terminal Value (Average of GGM and Exit Multiple)
      const tvGgm = (fcfPs * (1 + adjustedTerminalGrowth / 100)) / 
                    ((adjustedDiscountRate / 100) - (adjustedTerminalGrowth / 100));
      
      let baseExitMultiple = 12;
      if (moatScore >= 4) baseExitMultiple = 18;
      if (moatScore >= 5) baseExitMultiple = 22;
      if (predictabilityScore >= 4) baseExitMultiple += 2;
      if (stabilityScore <= 2) baseExitMultiple -= 3; 
      if (stabilityScore === 3) baseExitMultiple -= 1;
      
      const tvMultiple = fcfPs * baseExitMultiple;
      const weightGgm = (moatScore >= 5) ? 0.6 : (moatScore >= 4 && stabilityScore >= 4) ? 0.5 : 0.3;
      const tv = (tvGgm * weightGgm) + (tvMultiple * (1 - weightGgm));
      const pvTv = tv / Math.pow(1 + adjustedDiscountRate / 100, 10);
      
      const evPs = pvSum + pvTv;
      const netDebtPs = (analysis.netDebt || 0) / (analysis.sharesOutstanding || 1);
      const ivps = Math.max(0, evPs - netDebtPs);
      
      return { ivps, projections: yearProjections, pvTerminalValue: pvTv * analysis.sharesOutstanding, enterpriseValue: evPs * analysis.sharesOutstanding, exitMultiple: baseExitMultiple, fcfPerShare };
    };

    // Smarter Valuation: Growth Caps
    const cappedBaseGrowth = Math.min(baseGrowth, 15);
    const cappedStrategicGrowth = Math.min(strategicGrowth, 20);
    
    // Scenarios for the UI
    const bearValuation = calculateIntrinsicValue(cappedBaseGrowth * 0.7); // 30% haircut to growth
    const baseValuation = calculateIntrinsicValue(cappedBaseGrowth);
    const bullValuation = calculateIntrinsicValue(cappedStrategicGrowth);
    
    // Use Base Valuation as the primary Intrinsic Value (Conservative)
    let intrinsicValuePerShare = baseValuation.ivps;
    let strategicValuePerShare = bullValuation.ivps;
    
    // Final Intrinsic Value: Blending our DCF, FMP's DCF, and the AI's Oracle Judgment
    if (aiSuggestedIV > 0) {
      intrinsicValuePerShare = aiSuggestedIV;
    } else if (fmpCustomDcf > 0) {
      const weight = (moatScore >= 5) ? 0.7 : 0.5;
      intrinsicValuePerShare = (baseValuation.ivps * weight) + (fmpCustomDcf * (1 - weight));
      
      if (moatScore >= 5 && baseValuation.ivps > fmpCustomDcf * 1.2) {
        intrinsicValuePerShare = (baseValuation.ivps * 0.8) + (fmpCustomDcf * 0.2);
      }
    }

    // Ensure strategic upside reflects the blend
    strategicValuePerShare = Math.max(bullValuation.ivps, intrinsicValuePerShare * 1.2);
    
    const engineBearIV = analysis?.scenarios?.bear?.intrinsicValuePerShare ?? 0;
    const engineBullIV = analysis?.scenarios?.bull?.intrinsicValuePerShare ?? 0;

    const scenarios = {
      // Bear: use engine's locked bear scenario IV; fall back to hook calculation
      bear: engineBearIV > 0 ? engineBearIV : (fmpCustomDcf > 0 ? fmpCustomDcf * 0.85 : bearValuation.ivps),
      // Base: already engine IV
      base: intrinsicValuePerShare,
      // Bull: use engine's locked bull scenario IV; fall back to hook calculation
      bull: engineBullIV > 0 ? engineBullIV : strategicValuePerShare,
    };
    
    // Calculate Fair Value Range based on user requirements
    // Base rule: ±15%
    // High quality: ±10%
    // Uncertain/cyclical: ±20–30%
    // Factors: Moat (analysis.scorecard.moat), Predictability (analysis.scorecard.longTerm), Stability (analysis.scorecard.stability)
    
    const currentMoatScore = mapScoreToNumber('moat', analysis.scorecard?.moat);
    const currentPredictabilityScore = mapScoreToNumber('longTerm', analysis.scorecard?.longTerm);
    const currentStabilityScore = mapScoreToNumber('stability', analysis.scorecard?.stability);
    
    // Average quality score (1 to 5)
    const qualityScore = (currentMoatScore + currentPredictabilityScore + currentStabilityScore) / 3;
    
    // Map quality score to estimation error (5 -> 0.10, 1 -> 0.30)
    let estimationError = 0.30 - (qualityScore - 1) * (0.20 / 4);
    
    // Macro adjustment: If Market Indicator is high (> 150%), tighten the buy zone (increase error or lower buy price)
    const marketIndicatorValue = marketContext?.buffettIndicator?.value || 100;
    const isOverheated = marketIndicatorValue > 150;
    let macroAdjustment = 0;
    if (isOverheated) {
      // Tighten buy zone by increasing the "error" on the downside or just applying a macro discount
      estimationError = Math.min(0.40, estimationError + 0.05);
      macroAdjustment = 10; // Require 10% more margin of safety
    }

    const fairValueLow = intrinsicValuePerShare * (1 - estimationError);
    const fairValueHigh = intrinsicValuePerShare * (1 + estimationError);
    
    // Smarter Valuation: Quality-Adjusted Margin of Safety
    // Munger: "A great business at a fair price is better than a fair business at a great price."
    // But cyclical businesses ALWAYS need a margin of safety.
    const isHighQuality = moatScore >= 4 && predictabilityScore >= 4;
    const isStable = stabilityScore >= 4;
    const qualityMosReduction = (moatScore >= 5 && predictabilityScore >= 5) ? 25 : (isHighQuality && isStable) ? 15 : (isHighQuality) ? 10 : 0; 
    
    // [BUYZONE-CANONICAL] (Tuning v2.93 §61.11, ratified R-3 Stage 1): the server
    // stamps the canonical primary entry trigger on the analysis at pipeline time
    // (analysis.canonicalBuyZone) — the single source of truth, snapshot-consistent
    // with the price. The local formula below survives ONLY as (a) a legacy fallback
    // for saved analyses that predate the stamp and (b) a divergence VALIDATOR
    // (secondary source validates, never overrides). The user MOS slider remains a
    // what-if overlay via userScenarioBuyUnder and never mutates the Truth.
    const canonicalBuyZone = (analysis as any)?.canonicalBuyZone;
    const canonicalValid =
      canonicalBuyZone &&
      canonicalBuyZone.source === "server-canonical" &&
      typeof canonicalBuyZone.buyUnderPrice === "number" &&
      canonicalBuyZone.buyUnderPrice > 0 &&
      typeof canonicalBuyZone.effectiveMosPct === "number";
    const localEffectiveMarginOfSafety = Math.max(5, assumptions.marginOfSafety + macroAdjustment - qualityMosReduction);
    const localBuyUnderPrice = Math.max(0, intrinsicValuePerShare * (1 - localEffectiveMarginOfSafety / 100));
    const effectiveMarginOfSafety = canonicalValid ? canonicalBuyZone.effectiveMosPct : localEffectiveMarginOfSafety;
    const buyUnderPrice = canonicalValid ? canonicalBuyZone.buyUnderPrice : localBuyUnderPrice;
    if (canonicalValid) {
      // Validator compares against the DEFAULT base (25), not the slider value —
      // slider what-ifs are expected divergence, not defects.
      const validatorMos = Math.max(5, 25 + macroAdjustment - qualityMosReduction);
      const validatorBuyUnder = Math.max(0, intrinsicValuePerShare * (1 - validatorMos / 100));
      if (Math.abs(validatorBuyUnder - canonicalBuyZone.buyUnderPrice) > 0.02) {
        console.warn(
          `[BUYZONE-DIVERGENCE] ${analysis.ticker}: client validator $${validatorBuyUnder.toFixed(2)} ` +
            `(effMOS ${validatorMos}%) vs server canonical $${canonicalBuyZone.buyUnderPrice.toFixed(2)} ` +
            `(effMOS ${canonicalBuyZone.effectiveMosPct}%). Canonical wins; likely view-time vs ` +
            `analysis-time market state (buffett=${canonicalBuyZone.buffettIndicatorValue ?? "n/a"}).`,
        );
      }
    }

    // Classification Logic (Uncertainty Band)
    let label = "Fairly priced with modest upside";
    let statusColor = "amber";
    if (analysis.currentPrice < fairValueLow) {
      label = "Trading below intrinsic value";
      statusColor = "green";
    } else if (analysis.currentPrice > fairValueHigh) {
      label = "Premium valuation relative to risk";
      statusColor = "red";
    }

    // Rationale for Estimation Error
    const errorReasons = [];
    if (currentMoatScore < 3) errorReasons.push("weak competitive moat");
    if (currentPredictabilityScore < 3) errorReasons.push("low FCF predictability");
    if (currentStabilityScore < 3) errorReasons.push("low business stability");
    if (isOverheated) errorReasons.push("overheated macro environment");
    
    const estimationErrorRationale = errorReasons.length > 0 
      ? `Reflects a ±${safeToFixed(estimationError * 100, 0)}% uncertainty band due to ${errorReasons.join(", ")}.`
      : `Reflects a tight ±${safeToFixed(estimationError * 100, 0)}% uncertainty band due to high business quality and stability.`;

    // Rationale for Buy Under Price
    const isElite = moatScore >= 5 && predictabilityScore >= 5;
    const buyUnderRationale = isElite 
      ? `Tiered Entry Strategy: Start watching at ~$${safeToFixed(intrinsicValuePerShare * 0.95, 0)}, begin accumulating at ~$${safeToFixed(intrinsicValuePerShare * 0.85, 0)}, and conviction entry at ~$${safeToFixed(buyUnderPrice, 0)}. Reflects a ${effectiveMarginOfSafety}% required margin of safety for a core position.`
      : (() => { const displayMacroAdj = canonicalValid ? (canonicalBuyZone.macroAdjustmentPct ?? 0) : macroAdjustment; return `Reflects a ${effectiveMarginOfSafety}% required margin of safety on intrinsic value${displayMacroAdj > 0 ? ` (includes ${displayMacroAdj}% macro risk adjustment)` : ""} to account for competitive and cyclical risks. An attractive entry point would be in the $${Math.floor(buyUnderPrice * 0.95)} - $${Math.ceil(buyUnderPrice * 1.05)} range.`; })();

    // Decision Engine Logic (Action Rule)
    const currentMOS = (intrinsicValuePerShare > 0 && analysis.currentPrice > 0) 
      ? ((intrinsicValuePerShare - analysis.currentPrice) / intrinsicValuePerShare) * 100
      : 0;
    const isWeakMoat = moatScore < 3;
    const isCyclical = stabilityScore <= 3;
    const aiDecision = analysis.oracleFramework?.decision?.value?.toUpperCase() || "WATCH";

    const getArchetypeQualifier = (decision: string, archetypeCategory?: string): string => {
      if (decision !== "BUY" && decision !== "ACCUMULATE") return "";
      switch (archetypeCategory) {
        case "Turnaround":
        case "Speculative Growth":
          return " (Speculative)";
        case "Value Trap Risk":
          return " (High Risk)";
        case "Challenger / Share Gainer":
          return " (Emerging)";
        default:
          return "";
      }
    };

    // Safety Check: If AI says BUY but price is significantly above fair value, downgrade to WATCH
    // Exception: Inevitable Compounders in Accumulation Mode
    const isSignificantlyOvervalued = analysis.currentPrice > fairValueHigh * 1.15; // 15% above the top of the fair value range
    const isInevitable = analysis.isGrowthStructurallyInevitable || analysis.isInevitableCompounderOverride;
    
    // Deceleration & Volatility — uncertainty band only
    // NOTE: intrinsicValuePerShare is engine-locked when engineIV > 0.
    // These signals widen the uncertainty band and tighten the buy-under price.
    // They must NEVER mutate the IV itself — doing so breaks internal consistency
    // between the memo narrative (reads analysis.scenarios.base directly) and every
    // dcfModel.intrinsicValuePerShare consumer (dashboard pill, ValuationTab, MemoTab).
    const isDecelerating = analysis.isDecelerating || false;
    const volatilityScore = analysis.volatilityScore || 0;

    if (isDecelerating) {
      // intrinsicValuePerShare *= 0.85  ← REMOVED: was silently overwriting engine-locked IV
      estimationError += 0.05;
    }

    if (volatilityScore > 70) {
      estimationError += 0.10; // Increase uncertainty band for noisy signals
    }

    // Hard Gating: Narrative Tier cannot exceed Monetization Tier
    const newRevenueContribution = analysis.newRevenueContribution || 0;
    const isNarrativeGated = newRevenueContribution < 10;

    // Value Trap Constraint & Overvaluation Overrides
    let effectiveAiDecision = aiDecision;
    
    // Hard Logic Overrides (Valuations MUST override AI sentiment)
    const isOvervalued = analysis.currentPrice > intrinsicValuePerShare;
    const valuationRatio = intrinsicValuePerShare > 0 ? analysis.currentPrice / intrinsicValuePerShare : 1;
    const isSlightlyAboveIntrinsic = valuationRatio > 1 && valuationRatio <= 1.10;
    const isSignificantlyAboveIntrinsic = valuationRatio > 1.10;

    // [VERDICT-PASSTHROUGH] (Tuning v2.92): client-side verdict overrides removed.
    // The server decision matrix (agentPipeline T1/T2 gates) is the single verdict
    // authority; it is telemetered and drives the Verdict Tracker. Re-deriving here
    // caused display/telemetry divergence (CRM: server BUY, display FAT PITCH).

    // Determine if it's a FAT PITCH (High quality, high MOS, non-cyclical or cyclical but extremely cheap)
    // [VERDICT-PASSTHROUGH] FAT PITCH promotion removed — server matrix owns promotion.

    // Align Classification Label with AI Decision
    if (effectiveAiDecision === "FAT PITCH") {
      label = "Oracle FAT PITCH Directive";
      statusColor = "green";
    } else if (effectiveAiDecision === "ACCUMULATE") {
      label = `Oracle ACCUMULATE${getArchetypeQualifier("ACCUMULATE", analysis.archetype?.category)} Directive`;
      statusColor = "green";
    } else if (effectiveAiDecision === "CORE HOLD" || effectiveAiDecision === "HOLD") {
      label = "Oracle CORE HOLD Directive";
      statusColor = "amber"; // Changed to amber to distinguish from BUY
    } else if (effectiveAiDecision === "BUY") {
      label = `Oracle BUY${getArchetypeQualifier("BUY", analysis.archetype?.category)} Directive`;
      statusColor = "green";
    } else if (effectiveAiDecision.includes("AVOID")) {
      label = "Oracle AVOID Directive Issued";
      statusColor = "red";
    } else if (effectiveAiDecision.includes("PASS")) {
      label = "Oracle PASS Directive Issued";
      statusColor = "amber";
    } else {
      // Fallback for any other state
      if (analysis.currentPrice < fairValueLow) {
        label = "Trading below intrinsic value";
        statusColor = "green";
      } else if (analysis.currentPrice > fairValueHigh) {
        label = "Premium valuation relative to risk";
        statusColor = "red";
      }
    }

    let decision: { action: string; color: string; reason: string; qualifier?: string } = {
      action: effectiveAiDecision === "PASS" ? "PASS" : effectiveAiDecision.includes("AVOID") ? "AVOID" : effectiveAiDecision,
      color: (effectiveAiDecision === "FAT PITCH" || effectiveAiDecision === "ACCUMULATE" || effectiveAiDecision === "BUY") ? "green" : (effectiveAiDecision.includes("AVOID") || (effectiveAiDecision === "PASS" && currentMOS < -15)) ? "red" : "amber",
      reason: "", // Will be set below
      qualifier: getArchetypeQualifier(effectiveAiDecision, analysis.archetype?.category)
    };

    // Prioritize AI Decision to ensure total consistency across Dashboard, Classification, and Memo
    const validAiDecisions = ["BUY", "HOLD", "CORE HOLD", "ACCUMULATE", "FAT PITCH", "PASS", "AVOID", "WATCH"];
    const isExplicitAiDecision = validAiDecisions.includes(effectiveAiDecision);

    if (isExplicitAiDecision) {
      decision = {
        action: effectiveAiDecision,
        color: (effectiveAiDecision === "AVOID") ? "red" : (effectiveAiDecision === "PASS" || effectiveAiDecision === "WATCH" || effectiveAiDecision === "CORE HOLD" || effectiveAiDecision === "HOLD") ? "amber" : "green",
        reason: effectiveAiDecision === "FAT PITCH"
          ? `The Oracle has identified a FAT PITCH. High business quality combined with a massive margin of safety (${safeToFixed(currentMOS, 0)}%) makes this a rare opportunity to swing hard.`
          : effectiveAiDecision === "ACCUMULATE" 
            ? `The Oracle has issued an ACCUMULATE directive. While trading at a technical premium ($${safeToFixed(analysis.currentPrice, 2)} vs $${safeToFixed(intrinsicValuePerShare, 2)} IV), the business's structural quality justifies a starter position (2-5%) to be added to on weakness.`
            : effectiveAiDecision === "CORE HOLD" || effectiveAiDecision === "HOLD"
              ? `The Oracle has issued a CORE HOLD directive. Strategic quality justifies holding, but the current price ($${safeToFixed(analysis.currentPrice, 2)}) is above intrinsic value ($${safeToFixed(intrinsicValuePerShare, 2)}). Maintain core holdings but avoid new major allocations here.`
              : effectiveAiDecision === "BUY"
                ? `The Oracle has issued a BUY${getArchetypeQualifier("BUY", analysis.archetype?.category)} directive. ${
                    analysis.archetype?.category === "TURNAROUND / RE-INFLECTION CANDIDATE"
                      ? "This is a speculative position. The valuation math is compelling but execution risk is elevated. Size accordingly (1-3% max) and monitor thesis killers closely."
                      : "The current valuation offers an attractive entry point with a meaningful margin of safety."
                  }`
                : effectiveAiDecision === "AVOID"
                  ? `The Oracle advises to AVOID this security. ${currentMOS <= 0 ? `Security is overvalued by ${safeToFixed(Math.abs(currentMOS), 0)}%.` : `Despite a technical margin of safety, structural risks or moat erosion make this a dangerous proposition.`}`
                  : `The Oracle has issued a ${effectiveAiDecision} directive. ${analysis.passConfidenceScore ? `Confidence score: ${analysis.passConfidenceScore}%.` : ""} ${currentMOS > 0 ? `While trading at a technical discount (${safeToFixed(currentMOS, 0)}%), structural risks or business unknowability justify this decision.` : `Security is currently overvalued or lacks a clear investment thesis.`}`
      };
      
      // Override reason for overvalued elite businesses that were forced to CORE HOLD
      if (isOvervalued && (effectiveAiDecision === "CORE HOLD" || effectiveAiDecision === "HOLD")) {
         decision.reason = `Overvaluation Discipline: The current price ($${safeToFixed(analysis.currentPrice, 2)}) is above the calculated intrinsic value ($${safeToFixed(intrinsicValuePerShare, 2)}). While an elite brand, the margin of safety is absent. Wait for a price below $${safeToFixed(buyUnderPrice, 2)} before adding new capital.`;
      }
    } else if (effectiveAiDecision.includes("AVOID")) {
      decision = {
        action: "AVOID",
        color: "red",
        reason: `The Oracle advises to AVOID this security. ${currentMOS <= 0 ? `Security is overvalued by ${safeToFixed(Math.abs(currentMOS), 0)}%.` : `Despite a technical margin of safety, structural risks or moat erosion make this a dangerous proposition.`}`
      };
    } else if (effectiveAiDecision.includes("PASS")) {
      const isValueTrap = !isHighQuality && currentMOS > 20;
      decision = {
        action: "PASS",
        color: "amber",
        reason: isValueTrap
          ? `Value Trap Alert: While trading at a significant technical discount (${safeToFixed(currentMOS, 0)}%), the mediocre business quality (Moat: ${analysis.oracleFramework?.moat || 'Standard'}) and high capital intensity make this a dangerous proposition. Time is the enemy of the mediocre business.`
          : `The Oracle has issued a PASS directive. ${analysis.passConfidenceScore ? `Confidence score: ${analysis.passConfidenceScore}%.` : ""} ${currentMOS > 0 ? `While trading at a technical discount (${safeToFixed(currentMOS, 0)}%), structural risks or business unknowability justify a pass.` : `Security is currently overvalued or lacks a clear investment thesis.`}`
      };
    } else if (aiDecision.includes("BUY") && !isHighQuality) {
      // Special case: AI said BUY but we downgraded due to quality (Value Trap protection)
      decision = {
        action: "WATCH",
        color: "amber",
        reason: `The Oracle sees value, but the business quality does not meet the "High" threshold required for a BUY directive. Mediocre businesses at a discount are often value traps. Exercise extreme caution.`
      };
    } else if (aiDecision.includes("BUY") && isSignificantlyOvervalued) {
      // Special case: AI said BUY but we downgraded it
      decision = {
        action: "WATCH",
        color: "amber",
        reason: `The Oracle is bullish on the business, but the current price ($${safeToFixed(analysis.currentPrice, 2)}) is significantly above the fair value high ($${safeToFixed(fairValueHigh, 2)}). Exercise discipline and wait for a pullback.`
      };
    } else if (currentMOS >= effectiveMarginOfSafety && !isOverheated) {
      decision = {
        action: effectiveAiDecision === "ACCUMULATE" ? "ACCUMULATE" : "CORE HOLD",
        color: "green",
        reason: `Significant margin of safety identified (${safeToFixed(currentMOS, 0)}%). Current price is below the target entry point of $${safeToFixed(buyUnderPrice, 2)}.`
      };
    } else if (currentMOS <= 0) {
      decision = {
        action: "AVOID",
        color: "red",
        reason: isWeakMoat 
          ? `Security is overvalued by ${safeToFixed(Math.abs(currentMOS), 0)}% and lacks a durable competitive advantage (Weak Moat). Wait for a price below $${safeToFixed(buyUnderPrice, 2)}.`
          : `Security is currently trading ${safeToFixed(Math.abs(currentMOS), 0)}% above its estimated intrinsic value. An attractive entry point would be ~$${safeToFixed(buyUnderPrice, 2)}.`
      };
    } else if (currentMOS >= effectiveMarginOfSafety && isOverheated) {
      decision = {
        action: "WATCH",
        color: "amber",
        reason: `Attractive valuation (${safeToFixed(currentMOS, 0)}% MOS), but macro indicators (Market Indicator at ${safeToFixed(marketIndicatorValue, 0)}%) suggest an overheated market. Exercise caution.`
      };
    } else if (currentMOS < effectiveMarginOfSafety) {
      decision = {
        action: "WATCH",
        color: "amber",
        reason: `Insufficient Margin of Safety: Price is within the fair value range but lacks the required ${effectiveMarginOfSafety}% buffer. Target entry: ~$${safeToFixed(buyUnderPrice, 2)}.`
      };
    }

    decision.qualifier = getArchetypeQualifier(decision.action, analysis.archetype?.category);

    return {
      fairValueLow,
      fairValueHigh,
      projections: baseValuation.projections,
      intrinsicValuePerShare,
      strategicValuePerShare,
      isEstimable,
      buyUnderPrice,
      effectiveMarginOfSafety,
      enterpriseValue: baseValuation.enterpriseValue,
      pvTerminalValue: baseValuation.pvTerminalValue,
      totalPv: baseValuation.enterpriseValue - baseValuation.pvTerminalValue,
      fairValueRange: {
        low: fairValueLow,
        high: fairValueHigh,
        errorPercent: estimationError * 100,
        rationale: estimationErrorRationale
      },
      strategicUpsideRange: {
        low: fairValueHigh,
        high: Math.max(fairValueHigh + 0.01, scenarios.bull * (1 + estimationError))
      },
      strategicDownsideRange: {
        low: Math.min(fairValueLow - 0.01, scenarios.bear * (1 - estimationError)),
        high: fairValueLow
      },
      buyUnderRationale,
      valuationStatus: {
        diffPercent: analysis.currentPrice > 0 ? ((intrinsicValuePerShare - analysis.currentPrice) / analysis.currentPrice) * 100 : 0,
        label,
        statusColor,
        isUndervalued: analysis.currentPrice < fairValueLow
      },
      premiums: {
        durability: durabilityPremium,
        optionality: optionalityPremium,
        qualityDiscount: qualityPremium,
        terminalGrowth: adjustedTerminalGrowth
      },
      decision,
      scenarios,
      fcfPerShare,
      exitMultiple: baseValuation.exitMultiple,
      moatScore,
      predictabilityScore,
      userScenarioIV: baseValuation.ivps,
      userScenarioBuyUnder: Math.max(0, baseValuation.ivps * (1 - assumptions.marginOfSafety / 100)),
    };
  }, [analysis, assumptions, marketContext]);
}
